import React, {
  createContext,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from '../helpers/propTypes';

function set(obj, path, value) {
  const paths = path.split('.');

  let current = obj;
  for (let i = 0; i < paths.length - 1; i += 1) {
    current[paths[i]] = current[paths[i]] || {};
    current = current[paths[i]];
  }

  current[paths[paths.length - 1]] = value;
}

const StateContext = createContext();

export function GlobalStateProvider({ initialKeys, children }) {
  // Used to force rerender
  const [b, setB] = useState(0);

  const initialState = useMemo(() =>
    initialKeys.reduce((acc, key) => ({ ...acc, [key]: {} }), {}),
  );

  const state = useRef(initialState);

  const setQueue = useRef([]);
  const setState = (path, value, skipRerender) => {
    if (!skipRerender) {
      setQueue.current.push([path, value]);

      while (setQueue.current.length > 0) {
        const [queuedPath, queuedValue] = setQueue.current.shift();

        set(state.current, queuedPath, queuedValue);
      }

      setB(b + 1); // Forces a rerender
    } else {
      set(state.current, path, value);
    }
  };

  return (
    // eslint-disable-next-line react/jsx-no-constructed-context-values
    <StateContext.Provider value={{ state: state.current, setState }}>
      {children}
    </StateContext.Provider>
  );
}

GlobalStateProvider.propTypes = {
  children: PropTypes.node.isRequired,
  initialKeys: PropTypes.arrayOf(PropTypes.string),
};

GlobalStateProvider.defaultProps = {
  initialKeys: [],
};

export function useGlobalScope(scope) {
  const { state, setState } = useContext(StateContext);

  const localState = state[scope] || {};

  // Works almost exactly the same as useState.
  // Returns [value, setter, propertySetter]
  // The `propertySetter` can be used to set nested properties. `propertySetter('foo.bar', 'baz');`
  // would result in `scope.property.foo.bar` being equal to baz.
  const useGlobalState = (property, defaultValue) => {
    let value = localState[property];

    if (!Object.prototype.hasOwnProperty.call(localState, property)) {
      if (typeof defaultValue === 'function') value = defaultValue();
      else value = defaultValue || null;

      setState(`${scope}.${property}`, value, true);
    }

    return [
      value,
      (newValue) => setState(`${scope}.${property}`, newValue),
      (path, newValue) => setState(`${scope}.${property}.${path}`, newValue),
    ];
  };

  return useGlobalState;
}

export function useFullGlobalState() {
  const { state, setState } = useContext(StateContext);

  return [state, setState];
}
